import React, {useEffect, useState} from "react";
import {Button} from "react-bootstrap";
import {toast} from "react-toastify";
import "../../style/order/order_online_send.scss";
import {post} from "../utils/FetchUtil";
import {url} from "../utils/UrlsUtil";
import {useLoading} from "../components/loading/LoadingProvider";

//MessageMethodConst
const METHOD_MAIL = "0";
const METHOD_LINE = "1";
const METHOD_COPY = "3";

/**
 * PNGタイプ：QRファイル（ZIP）ダウンロード（楽天の注文で売上済のみ表示）
 */
export const OrderPngDownload: React.FC<{dtl: OrderInputDetailDto}> = ({dtl}) => {
    const { showLoading, hideLoading } = useLoading();

    const onClickDownload = async () => {
        showLoading();
        try{
            const response = await fetch(url(`/api/order/png/zip?slipnumber=${encodeURIComponent(dtl.slipnumber)}`));
            if(!response.ok){
                toast.error(await response.text() || "QRファイルのダウンロードができません。");
                return;
            }
            const blob = await response.blob();
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `QR_${dtl.slipnumber}.zip`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(link.href);
        }catch (e){
            console.log(e);
            toast.error("QRファイルのダウンロードができません。");
        }finally {
            hideLoading();
        }
    };

    if(!dtl.png_downloadable){
        return null;
    }
    return (
        <div className="online-send-area png-download">
            <Button variant="secondary"
                    className="btn-png-download"
                    onClick={onClickDownload}>
                QRファイルダウンロード（ZIP）
            </Button>
        </div>
    );
};

interface SendMethodIconProps{
    method: string;
    iconName: string;
    label: string;
    card: OrderInputCardDto;
    onClick: () => void;
}

/**
 * 送信方法のアイコン（送信済：塗りつぶし＋チェック、未送信：枠線のみ）
 */
const SendMethodIcon: React.FC<SendMethodIconProps> = ({method, iconName, label, card, onClick}) => {
    const sent = card.sent_methods?.includes(method) ?? false;
    return (
        <button type="button"
                className={`send-method-icon method-${method} ${sent ? "sent" : ""}`}
                title={`${label}${sent ? "（送信済）" : "（未送信）"}`}
                aria-label={`${label}${sent ? "（送信済）" : "（未送信）"}`}
                onClick={onClick}>
            <span className="icon-circle">
                <i className={`${sent ? "fa-solid" : "fa-regular"} ${iconName}`}></i>
                {sent && <i className="fa-solid fa-circle-check sent-badge"></i>}
            </span>
        </button>
    );
};

/**
 * eSelectタイプ：URL送信履歴（ログインID毎にコピー・メール・LINEでURLを送信）※楽天の注文のみ表示
 */
export const OrderUrlSendHistory: React.FC<{dtl: OrderInputDetailDto}> = ({dtl}) => {
    const [cardList, setCardList] = useState<OrderInputCardDto[]>(dtl.card_list ?? []);
    const [open, setOpen] = useState(false);
    //コピーしたログインID（未送信の場合、送信済チェックを可能にする）
    const [copiedCardEncs, setCopiedCardEncs] = useState<string[]>([]);
    //送信済の登録中（二重登録を防ぐ）
    const [savingCardEncs, setSavingCardEncs] = useState<string[]>([]);

    useEffect(() => {
        setCardList(dtl.card_list ?? []);
    }, [dtl.card_list]);

    const sendText = (card: OrderInputCardDto) =>
        `下記URLからギフトをお受け取りください。\n${card.apply_url}\n\n受付番号：${dtl.slipnumber}\nログインID：${card.accept_id}`;

    const entrySent = (card: OrderInputCardDto, method: string) => {
        setSavingCardEncs(list => [...list, card.card_enc]);
        post<any>({
            action: "/api/order/card/sent",
            data: {card_enc: card.card_enc, message_method: method},
        })
            .then((data) => {
                if(data && data["card"]){
                    setCardList(list => list.map(c => c.card_enc === card.card_enc ? data["card"] : c));
                }
            })
            .catch((reason) => {
                toast.error(reason.message || "送信済の登録ができません。");
            })
            .finally(() => {
                setSavingCardEncs(list => list.filter(enc => enc !== card.card_enc));
            });
    };

    const copyText = async (text: string) => {
        if(navigator.clipboard && window.isSecureContext){
            try{
                await navigator.clipboard.writeText(text);
                return;
            }catch (e){
                //権限がない場合などは下記の方法でコピーする
                console.log(e);
            }
        }
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        const copied = document.execCommand("copy");
        textArea.remove();
        if(!copied){
            throw new Error("copy failed");
        }
    };

    const onClickCopy = async (card: OrderInputCardDto) => {
        try{
            await copyText(card.apply_url);
            toast.success("コピーしました。");
            //コピーだけでは送信済にしない（送信後に送信済チェックで登録する）
            setCopiedCardEncs(list => list.includes(card.card_enc) ? list : [...list, card.card_enc]);
        }catch (e){
            console.log(e);
            toast.error("コピーができません。");
        }
    };

    const onClickMail = (card: OrderInputCardDto) => {
        window.location.href = `mailto:?subject=${encodeURIComponent("ギフトのお届け")}&body=${encodeURIComponent(sendText(card))}`;
        entrySent(card, METHOD_MAIL);
    };

    const onClickLine = (card: OrderInputCardDto) => {
        window.open(`https://line.me/R/share?text=${encodeURIComponent(sendText(card))}`, "_blank", "noopener");
        entrySent(card, METHOD_LINE);
    };

    const sentMethodName = (method?: string) => {
        switch (method){
            case METHOD_MAIL: return "メール";
            case METHOD_LINE: return "LINE";
            case METHOD_COPY: return "コピー";
            default: return "";
        }
    };

    //楽天の注文のみ表示
    if(!dtl.rakuten_order || cardList.length === 0){
        return null;
    }
    const sentCount = cardList.filter(card => card.sent).length;
    return (
        <div className="url-send-history">
            <button type="button" className="url-send-history-header" aria-expanded={open} onClick={() => setOpen(!open)}>
                <span className="title">URL送信履歴</span>
                <span className="count">送信済 {sentCount} / {cardList.length}件</span>
                <span className={`toggle-icon ${open ? "open" : ""}`}>&#9660;</span>
            </button>
            {open && <table className="url-send-history-table">
                <thead>
                    <tr>
                        <th>ログインID</th>
                        <th>状況</th>
                        <th>URLコピー / メール / LINE</th>
                    </tr>
                </thead>
                <tbody>
                    {cardList.map(card =>
                        <tr key={card.card_enc} className={card.sent ? "sent" : ""}>
                            <td className="login-id">{card.accept_id}</td>
                            <td className="status" title={card.sent ? `${sentMethodName(card.sent_method)} ${card.sent_datetime ?? ""}` : ""}>
                                {/* 送信済：✓ 送信済（変更不可）。未送信：コピー後にチェックを表示し、チェックすると送信済を登録 */}
                                {card.sent ? <>&#10003; 送信済</>
                                    : copiedCardEncs.includes(card.card_enc) ?
                                        <label className="sent-check">
                                            <input type="checkbox"
                                                   checked={false}
                                                   disabled={savingCardEncs.includes(card.card_enc)}
                                                   onChange={() => entrySent(card, METHOD_COPY)}/>
                                            送信済
                                        </label>
                                        : "未送信"}
                            </td>
                            <td className="send-methods">
                                <SendMethodIcon method={METHOD_COPY} iconName="fa-copy" label="コピー" card={card} onClick={() => onClickCopy(card)}/>
                                <SendMethodIcon method={METHOD_MAIL} iconName="fa-envelope" label="メール" card={card} onClick={() => onClickMail(card)}/>
                                <SendMethodIcon method={METHOD_LINE} iconName="fa-comment-dots" label="LINE" card={card} onClick={() => onClickLine(card)}/>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>}
        </div>
    );
};
